import { Link } from '@tanstack/react-router';
import { FileRoutesByTo } from '@/routeTree.gen';
import { ReactNode, useMemo } from 'react';
import {
  IconOrganisations,
  IconDashboard,
  IconHamburger,
  IconOrganisation,
  IconPeriods,
  IconTicket,
  IconTravellers,
  IconReports,
  IconPayers,
} from '@/icons';
import { cn } from '@/helpers/classnames';
import { ViewPermissions } from '../Auth/ViewPermissions';
import { useTenantID } from '@/hooks/useTenantID';
import { useAuth } from '@/stores/auth';
import { useListOrganizations } from '@/api/trafikportalen';
import { useListOrganizationApplications } from '@/api/trafikportalen/trafikportalenAPI';
import { useIncompletePayersCount } from '@/hooks/useIncompletePayersCount';
import { useOrganizationsMissingCurrentPeriod } from '@/hooks/useOrganizationsMissingCurrentPeriod';
import { NotificationDot } from '../NotificationDot';
import { useShowOrgLevelNav } from '@/hooks/useShowOrgLevelNav';
import { useStudentsDisabled } from '@/contexts/ConfigContext';

export function BottomBar() {
  const { userProfile } = useAuth();
  const showOrgLevelNav = useShowOrgLevelNav();
  const studentsDisabled = useStudentsDisabled();

  if (!userProfile) {
    return null;
  }

  return (
    <nav
      aria-label="Huvudnavigation"
      className="z-10 flex h-16 w-full items-center justify-between border-t border-background-300 bg-background-50"
    >
      <MenuItem
        to="/$tenantId"
        icon={<IconDashboard />}
        title="Start"
        exactMatch={true}
      />
      {showOrgLevelNav && (
        <>
          <MenuItem
            to="/$tenantId/tickets"
            icon={<IconTicket />}
            title="Biljetter"
          />
          <MenuItem
            to="/$tenantId/travellers"
            icon={<IconTravellers />}
            title="Resenärer"
          />
          <MenuItem
            to="/$tenantId/departments"
            icon={<IconOrganisation />}
            title="Organisation"
          />
        </>
      )}
      <ViewPermissions permission="tenant:viewer">
        <OrganizationsMenuItem />
        {!studentsDisabled && <PeriodsMenuItem />}
        <MenuItem
          to="/$tenantId/reports"
          icon={<IconReports />}
          title="Rapporter"
        />
        <PayersMenuItem />
      </ViewPermissions>
      <MenuItem to="/$tenantId/menu" icon={<IconHamburger />} title="Meny" />
    </nav>
  );
}

const MenuItem = ({
  to,
  icon,
  title,
  exactMatch = false,
  notificationCount,
}: {
  to: keyof FileRoutesByTo;
  icon: ReactNode;
  title: string;
  exactMatch?: boolean;
  notificationCount?: number;
}) => {
  const tenantId = useTenantID();

  return (
    <Link
      className="relative flex size-full flex-col items-center justify-center hover:bg-background-200 active:opacity-80 [&>svg]:size-6"
      to={to}
      params={{ tenantId }}
      aria-label={title}
      activeOptions={{ exact: exactMatch }}
      activeProps={{ 'aria-current': 'page' as const }}
    >
      {({ isActive }: { isActive: boolean }) => (
        <>
          {icon}
          <div className="absolute right-1/4 top-2 animate-bounce">
            {notificationCount && notificationCount > 0 ? (
              <NotificationDot count={notificationCount} />
            ) : null}
          </div>
          <div
            aria-hidden="true"
            className={cn(
              'h-0 w-8 rounded-full bg-primary-500 transition-all',
              isActive && 'h-2',
            )}
          />
        </>
      )}
    </Link>
  );
};

const OrganizationsMenuItem = () => {
  const { data: organizationApplications } = useListOrganizationApplications();

  const submittedApplications = useMemo(() => {
    if (!organizationApplications) return 0;

    return organizationApplications.filter(
      (application) => application.status === 'submitted',
    ).length;
  }, [organizationApplications]);

  return (
    <MenuItem
      to="/$tenantId/organizations"
      icon={<IconOrganisations />}
      title="Organisationer"
      notificationCount={submittedApplications}
    />
  );
};

const PeriodsMenuItem = () => {
  const { data: organizations } = useListOrganizations();
  const { total } = useOrganizationsMissingCurrentPeriod(organizations);

  return (
    <MenuItem
      to="/$tenantId/periods"
      icon={<IconPeriods />}
      title="Perioder"
      notificationCount={total}
    />
  );
};

const PayersMenuItem = () => {
  const incompletePayersCount = useIncompletePayersCount();

  return (
    <MenuItem
      to="/$tenantId/payers"
      icon={<IconPayers />}
      title="Fakturamottagare"
      notificationCount={incompletePayersCount}
    />
  );
};
